// Shared-timer pill in the control bar (next to Files) — everyone sees it.
// Ticks in ISOLATION: a local interval updates only this component's text, so
// the per-second countdown never re-renders a video tile (§9.3), mirroring the
// Elapsed / MeetingCountdown badges. Reads the low-frequency store state and
// computes live remaining from the receipt-anchored value. Digital-watch
// numerals in green (see .bz-timer-* in styles.css).

import { memo, useEffect, useState } from 'react';

import { liveRemaining, timerStore, useTimerStore } from '../store/timerStore';
import { IconTimer } from '../ui/icons';
import { SevenSeg } from './SevenSeg';

export function fmtTimer(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Live "MM:SS" text that ticks while running; static when paused/done. The
 * interval (not render) reads performance.now via liveRemaining. */
export function useTimerText(): string {
  const status = useTimerStore((s) => s.status);
  const remainingMs = useTimerStore((s) => s.remainingMs);
  const [text, setText] = useState(() => fmtTimer(remainingMs));
  useEffect(() => {
    const update = () => setText(fmtTimer(liveRemaining(timerStore.getState())));
    update();
    if (status !== 'running') return;
    const id = setInterval(update, 500);
    return () => clearInterval(id);
  }, [status, remainingMs]);
  return text;
}

export const TimerPill = memo(function TimerPill() {
  const status = useTimerStore((s) => s.status);
  const text = useTimerText();
  if (status === 'off') return null;
  return (
    <div className={`bz-timer-pill bz-timer-${status}`} title="Meeting timer">
      <IconTimer size={13} />
      <SevenSeg value={text} />
    </div>
  );
});
